import { logEvent } from '@eventbrite/legacy-infra.statsig';
import { type EventInteractionWithContext, EventInteractions } from '../types';
import { BaseInteractionsLogger } from './BaseInteractionsLogger';

/**
 * Data structure for Statsig logging
 */
type InteractionStatsigLoggerData = {
  name: string;
  metadata: {
    app: string;
    rank: string;
    isOnline: string;
    page: string;
    isPromoted: string;
    hasUrgencySignals: string;
    isFree: string;
  };
};

export class InteractionsStatsigLogger extends BaseInteractionsLogger<InteractionStatsigLoggerData> {
  protected allowedEvents: EventInteractions[] = [EventInteractions.Impression, EventInteractions.Click];

  /**
   * Filters out non-promoted events
   *
   * @param interaction - The interaction to check
   */
  protected filterEventsBy(interaction: EventInteractionWithContext): boolean {
    return Boolean(interaction.event.isPromoted);
  }

  /**
   * Transforms an interaction for Statsig logging
   *
   * @param interaction - The interaction to transform
   * @returns Statsig-specific data format
   */
  protected transformInteraction(interaction: EventInteractionWithContext): InteractionStatsigLoggerData {
    const urgencySignals = interaction.displayContext?.urgencySignals || [];
    const hasUrgencySignals = urgencySignals.length > 0;

    return {
      name: this.getStatsigEventName(interaction),
      metadata: {
        app: 'web',
        rank: `${interaction.place.position}`,
        isOnline: `${interaction.event.isOnlineEvent}`,
        page: `${interaction.place.page}`,
        isPromoted: `${interaction.event.isPromoted}`,
        hasUrgencySignals: `${hasUrgencySignals}`,
        isFree: `${interaction.event.isFree}`,
      },
    };
  }

  /**
   * Logs interactions to Statsig
   *
   * @param transformedInteractions - Array of transformed interactions
   */
  protected log(transformedInteractions: InteractionStatsigLoggerData[]): void {
    transformedInteractions.forEach((event) => logEvent(event.name, undefined, event.metadata));
  }

  private getStatsigEventName(interaction: EventInteractionWithContext): string {
    const dataPoints = [
      interaction.place.name,
      interaction.place.subInterface?.name,
      interaction.action,
      'Promoted',
      'Event(s)',
    ].filter((str) => !!str) as string[];

    return dataPoints.map(snakeToTitle).join(' - ');
  }
}

/**
 * Converts snake_case text to Title Case
 *
 * @param snakeCaseText - Text in snake_case format
 * @returns Text in Title Case format
 */
function snakeToTitle(snakeCaseText: string): string {
  const words = snakeCaseText.split('_');
  const capitalizedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1));
  return capitalizedWords.join(' ');
}
